import { useQuery } from '@apollo/client';
import { Balance } from 'components/Balance/Balance';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { DocumentsTab } from 'components/DocumentsTabs/DocumentsTabs';
import { Icon } from 'components/Icon/Icon';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import { SortFilter } from 'components/SortFilter/SortFilter';
import { Status } from 'components/Status/Status';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import { normalizeArray } from 'lib/utils';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from 'Routes';
import { COMPANIES_FULL, COMPANIES_BALANCES } from 'services/companies/query';

const SORT = [
    { value: 'A_Z', label: 'A-Z' },
    { value: 'Z_A', label: 'Z-A' },
    { value: 'HIGHEST_BALANCE', label: 'Highest balance' },
    { value: 'LOWEST_BALANCE', label: 'Lowest balance' },
];

export const CompanyConsumptions = () => {
    const { t } = useTranslation('documents');
    const { t: b } = useTranslation('translation');
    const [searchParams] = useSearchParams();
    const [allBalances, setAllBalances] = useState<any[]>([]);
    const [allCompanies, setAllCompanies] = useState<any[]>([]);
    const navigate = useNavigate();

    const { data, refetch, fetchMore } = useQuery(COMPANIES_FULL, {
        variables: { first: 100 },
    });

    // Separate query to fetch ALL active companies for totals calculation
    const { data: balancesData, fetchMore: fetchMoreBalances } = useQuery(COMPANIES_BALANCES, {
        variables: {
            status: ['ACTIVE'],
            first: 100,
            offset: 0,
        },
    });

    // Fetch all pages of balances for totals
    useEffect(() => {
        const fetchAllBalances = async () => {
            if (!balancesData?.companies) return;

            let allCompaniesData = normalizeArray(balancesData.companies);
            let hasNextPage = balancesData.companies.pageInfo?.hasNextPage;
            let offset = 100;

            while (hasNextPage) {
                const result = await fetchMoreBalances({
                    variables: {
                        offset,
                    },
                });

                const newCompanies = normalizeArray(result.data.companies);
                allCompaniesData = [...allCompaniesData, ...newCompanies];
                hasNextPage = result.data.companies.pageInfo?.hasNextPage;
                offset += 100;
            }

            setAllBalances(allCompaniesData);
        };

        fetchAllBalances();
    }, [balancesData, fetchMoreBalances]);

    // Fetch all pages of companies for the table
    useEffect(() => {
        const fetchAllCompanies = async () => {
            if (!data?.companies) return;

            const search = searchParams.get('search');
            const sort = searchParams.get('sort');

            let allCompaniesData = normalizeArray(data.companies);
            let hasNextPage = data.companies.pageInfo?.hasNextPage;
            let offset = 100;

            while (hasNextPage) {
                const result = await fetchMore({
                    variables: {
                        search,
                        sort,
                        offset,
                    },
                });

                const newCompanies = normalizeArray(result.data.companies);
                allCompaniesData = [...allCompaniesData, ...newCompanies];
                hasNextPage = result.data.companies.pageInfo?.hasNextPage;
                offset += 100;
            }

            setAllCompanies(allCompaniesData);
        };

        fetchAllCompanies();
    }, [data, fetchMore, searchParams]);

    // Refetch when search or sort changes
    useEffect(() => {
        const search = searchParams.get('search');
        const sort = searchParams.get('sort');

        refetch({
            search: search,
            sort: sort,
            offset: 0,
            first: 100,
        });
    }, [searchParams, refetch]);

    const totals = useMemo(() => {
        if (!allBalances.length) {
            return { totalBalance: 0, totalRemainderBalance: 0, currency: 'CHF' };
        }

        const totalBalance = allBalances.reduce((sum: number, company: any) => {
            return sum + parseFloat(company.balance?.amount || 0);
        }, 0);

        const totalRemainderBalance = allBalances.reduce((sum: number, company: any) => {
            return sum + parseFloat(company.remainderBalance || 0);
        }, 0);

        const currency = allBalances[0]?.balance?.currency || 'CHF';

        return { totalBalance, totalRemainderBalance, currency };
    }, [allBalances]);

    const openCompany = (id: string) => {
        navigate(AppRoutes.companyPreview.replace(':id', id));
    };

    return (
        <div>
            <Breadcrumbs crumbs={[b('breadcrumbs.dashboard'), t('companyConsumptionsTitle')]} />
            <DocumentsTab />
            <PageTitle>{t('companyConsumptionsTitle')}</PageTitle>

            <div className="flex gap-4 flex-wrap pb-4 justify-between items-center">
                <div className="flex gap-4 flex-wrap">
                    <SearchFilter placeholder={t('searchCompanyPlaceholder')} />
                    <SortFilter options={SORT} />
                </div>
                <div className="flex gap-6 px-4 py-2 bg-zinc-50 border rounded">
                    <div className="flex flex-col items-end">
                        <span className="text-sm text-zinc-600 font-medium">{t('balanceLabel')}</span>
                        <Balance balance={{ amount: totals.totalBalance, currency: totals.currency }} />
                    </div>
                    <div className="flex flex-col items-end">
                        <span className="text-sm text-zinc-600 font-medium">{t('remainderBalanceLabel')}</span>
                        <Balance balance={{ amount: totals.totalRemainderBalance, currency: totals.currency }} />
                    </div>
                </div>
            </div>

            <Table className="border">
                <TableHeader>
                    <TableRow>
                        <TableHead>{t('companyNameLabel')}</TableHead>
                        <TableHead>{t('companyIdLabel')}</TableHead>
                        <TableHead>{t('emailLabel')}</TableHead>
                        <TableHead>{t('balanceLabel')}</TableHead>
                        <TableHead>{t('remainderBalanceLabel')}</TableHead>
                        <TableHead>{t('totalBalanceLabel')}</TableHead>
                        <TableHead>{t('statusLabel')}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className="hover:cursor-pointer">
                    {allCompanies.length > 0 &&
                        allCompanies.map((company: any) => (
                            <TableRow key={company.id} className="text-zinc-700 hover:bg-rose-50">
                                <TableCell
                                    className="flex gap-2 items-center"
                                    onClick={() => openCompany(company.id)}
                                >
                                    <CompanyImage image={company.image} />
                                    {company.name || '-'}
                                </TableCell>
                                <TableCell onClick={() => openCompany(company.id)}>
                                    {company.companyId || '-'}
                                </TableCell>
                                <TableCell onClick={() => openCompany(company.id)}>{company.email || '-'}</TableCell>
                                <TableCell className="hidden md:table-cell" onClick={() => openCompany(company.id)}>
                                    <Balance balance={company.balance} />
                                </TableCell>
                                <TableCell className="hidden md:table-cell" onClick={() => openCompany(company.id)}>
                                    <Balance
                                        balance={{
                                            currency: company.balance?.currency || 'CHF',
                                            amount: company.remainderBalance || 0,
                                        }}
                                    />
                                </TableCell>
                                <TableCell className="hidden md:table-cell" onClick={() => openCompany(company.id)}>
                                    <Balance
                                        balance={{
                                            currency: company.balance?.currency || 'CHF',
                                            amount:
                                                parseFloat(company.balance?.amount || 0) +
                                                parseFloat(company.remainderBalance || 0),
                                        }}
                                    />
                                </TableCell>
                                <TableCell className="hidden md:table-cell" onClick={() => openCompany(company.id)}>
                                    <Status status={company.status} />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </div>
    );
};

const CompanyImage = ({ image }: any) => {
    return (
        <div className="bg-zinc-300 w-[32px] h-[32px] flex justify-center items-center">
            {image ? (
                <img src={`${image.url}?fit=facearea&facepad=2.5&w=32&h=32`} alt={image.alt} />
            ) : (
                <Icon name="building" className="text-zinc-50 h-[18px]" />
            )}
        </div>
    );
};
